import React, { useState } from 'react';
import { CheckCircleOutline as CheckIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config/api';
import { BRAND, CONTACT_EMAIL, usePageMeta } from './shared';

const ORG_TYPES = [
  'Fraternity',
  'Sorority',
  'Civic Organization',
  'Alumni Chapter',
  'Undergraduate Chapter',
  'Regional / Council Leadership',
  'National Organization',
  'Other',
];

const emptyForm = { name: '', email: '', organization: '', role: '', orgType: '', message: '' };

const Contact: React.FC = () => {
  usePageMeta('Request a demo');
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'err'>('idle');
  const [error, setError] = useState('');

  const set = (k: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim()) {
      setStatus('err');
      setError('Please fill in your name, email, and organization.');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/contact/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'konklave.app' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }
      setStatus('ok');
      setForm(emptyForm);
    } catch (err: any) {
      setStatus('err');
      setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <>
      <section className="page-head">
        <div className="container">
          <h1>Request a demo</h1>
          <p>
            See how {BRAND} works for an organization like yours, and get pricing for your size.
            Tell us a little about you and we’ll set up a walkthrough. Prefer email? Write to{' '}
            <a className="text-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </div>
      </section>

      <section className="page-body">
        <div className="container contact-grid">
          <div className="contact-aside">
            <h2>What to expect</h2>
            <p>A short walkthrough tailored to your organization, with no obligation.</p>
            <ul className="check-list">
              {[
                'A live tour of the member experience, directory, events, and more',
                `How your branding and chapter structure map onto ${BRAND}`,
                'Pricing tailored to your size and needs',
                'Answers on privacy, SSO, migration, and rollout',
              ].map((p) => (
                <li key={p}><CheckIcon aria-hidden="true" /> <span>{p}</span></li>
              ))}
            </ul>
          </div>

          <form className="form-card" onSubmit={submit}>
            {status === 'ok' && (
              <div className="form-msg ok" role="status">Thank you! We’ve received your request and will be in touch shortly.</div>
            )}
            {status === 'err' && <div className="form-msg err" role="alert">{error}</div>}

            <div className="field-row">
              <div className="field">
                <label htmlFor="c-name">Full name *</label>
                <input id="c-name" required value={form.name} onChange={set('name')} autoComplete="name" />
              </div>
              <div className="field">
                <label htmlFor="c-email">Work email *</label>
                <input id="c-email" required type="email" value={form.email} onChange={set('email')} autoComplete="email" />
              </div>
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="c-org">Organization *</label>
                <input id="c-org" required value={form.organization} onChange={set('organization')} autoComplete="organization" />
              </div>
              <div className="field">
                <label htmlFor="c-role">Your role</label>
                <input id="c-role" value={form.role} onChange={set('role')} placeholder="e.g. President, Director" />
              </div>
            </div>
            <div className="field">
              <label htmlFor="c-type">Organization type</label>
              <select id="c-type" value={form.orgType} onChange={set('orgType')}>
                <option value="">Select one…</option>
                {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="c-msg">Anything we should know?</label>
              <textarea id="c-msg" value={form.message} onChange={set('message')} placeholder="Tell us about your organization and what you’re looking for." />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Request a demo'}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Contact;
